import * as styles from '../FirstResponder.module.css';
import PreviewPagination from './PreviewPagination';
import {
  LiveClientFilter,
  LiveJobRow,
  LivePage,
  LiveTone,
} from './liveViewModel';
import { formatCountdown } from './useCountdown';

/*
 * Jobs card: client filter chips + the jobs table. STATELESS and
 * collection-free: it consumes a SERVER-SHAPED PAGE from the adapter (it
 * never receives the full jobs array and never slices), and both the selected
 * filter and page changes are callbacks to the data owner. Every interactive
 * element is a real native <button>; a <tr> is never an improvised control.
 *
 * "Workers available" is EXACTLY pool_after_gates. A non-null count is an
 * inspectable native button opening the job drilldown on its Workers
 * available section; null renders "—" and is not clickable.
 *
 * GRAVY-10373 Task 7 — a row with `clickable: false` (the real adapter's unrecorded 'watching'
 * rows, which have no persisted run to show — GET .../jobs/:id would 404 for them) renders plain,
 * non-interactive text instead of a button, with a `title` explaining why, and never calls
 * onSelectJob. Never a broken or empty modal.
 *
 * GRAVY-10373 M1 — `loadError` renders a small inline retry notice when a clicked row's job
 * detail/workers failed to load (a failure used to be a silent dead click). Optional and null in
 * the preview, which never fails; only the real connected container supplies it.
 *
 * GRAVY-10373 F8 — `pageError` is the same notice for the OTHER silent failure: the jobs page
 * fetch behind the filter chips and pagination. Two props rather than one because their Retry
 * buttons re-run different requests, and a Retry whose meaning depends on which failure happened
 * is no more trustworthy than the empty catch it replaced.
 */

const NOT_YET_EVALUATED_TITLE =
  'No evaluation has been recorded for this job yet';

const BADGE_TONE: Record<LiveTone, string | undefined> = {
  blue: styles.blue,
  amber: styles.amber,
  green: styles.green,
  red: styles.red,
  plain: undefined,
};

type Props = {
  page: LivePage<LiveJobRow>; // adapter-produced page — never the full list
  onPageChange: (page: number) => void;
  filters: LiveClientFilter[];
  selectedFilter: string;
  onSelectFilter: (key: string) => void;
  onSelectJob: (id: string, section?: 'overview' | 'workers') => void;
  elapsed: number;
  jobsLoading: boolean;
  loadError?: { message: string; onRetry: () => void } | null;
  pageError?: { message: string; onRetry: () => void } | null;
};

const JobsCard = ({
  page: { items: rows, pagination },
  onPageChange,
  filters,
  selectedFilter,
  onSelectFilter,
  onSelectJob,
  elapsed,
  jobsLoading,
  loadError,
  pageError,
}: Props) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <h3>Jobs</h3>
    </div>
    {loadError && (
      <div
        className={styles.section}
        data-testid="flp-jobs-load-error"
        role="alert"
      >
        <div className={styles.formErrors}>
          {loadError.message}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`}
            style={{ marginLeft: 8 }}
            onClick={loadError.onRetry}
          >
            Retry
          </button>
        </div>
      </div>
    )}
    {/* F8: the filter/pagination fetch's own failure — its own notice and its own Retry, because
        it retries a different request than loadError's (this page of the table, not a job open).
        The table below keeps rendering whatever page last loaded: a failed fetch reports itself,
        it never takes away what the operator could already read. */}
    {pageError && (
      <div
        className={styles.section}
        data-testid="flp-jobs-page-error"
        role="alert"
      >
        <div className={styles.formErrors}>
          {pageError.message}
          <button
            type="button"
            className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`}
            style={{ marginLeft: 8 }}
            onClick={pageError.onRetry}
          >
            Retry
          </button>
        </div>
      </div>
    )}
    <div className={styles.section}>
      <div className={styles.filters}>
        {filters.map((f) => {
          const active = selectedFilter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              className={`${styles.chip} ${active ? styles.chipActive : ''}`}
              data-testid={`flp-filter-${f.key}`}
              data-active={active ? 'true' : 'false'}
              aria-pressed={active}
              disabled={jobsLoading}
              onClick={() => onSelectFilter(f.key)}
            >
              {f.label}
              {f.watching !== null && f.filled !== null && (
                <span className={styles.chipCnt}>
                  {f.watching} watching · {f.filled} filled
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
    {jobsLoading ? (
      <div className={styles.section} data-testid="flp-jobs-loading">
        <span className={styles.muted}>Loading jobs…</span>
      </div>
    ) : rows.length === 0 ? (
      <div className={styles.section} data-testid="flp-jobs-empty">
        <span className={styles.muted}>No jobs in the window.</span>
      </div>
    ) : (
      <>
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Job</th>
                <th>Client</th>
                <th>Status</th>
                <th>Trigger</th>
                <th>Workers available</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((j) => (
                <tr
                  key={j.id}
                  className={styles.zebra}
                  data-testid={`flp-job-row-${j.id}`}
                >
                  <td className={styles.strong}>
                    {j.clickable ? (
                      <button
                        type="button"
                        className={styles.btnPlain}
                        style={{
                          fontSize: 'inherit',
                          color: 'inherit',
                          fontWeight: 500,
                        }}
                        data-testid={`flp-job-open-${j.id}`}
                        onClick={() => onSelectJob(j.id)}
                      >
                        {j.title}
                      </button>
                    ) : (
                      <span
                        data-testid={`flp-job-open-${j.id}`}
                        title={NOT_YET_EVALUATED_TITLE}
                      >
                        {j.title}
                      </span>
                    )}
                  </td>
                  <td className={styles.muted}>{j.client}</td>
                  <td>
                    <span
                      className={`${styles.badge} ${BADGE_TONE[j.tone] ?? ''}`}
                    >
                      {j.stateLabel}
                    </span>
                  </td>
                  <td>
                    {j.next.kind === 'countdown' ? (
                      <span
                        className={styles.strong}
                        data-testid={`flp-countdown-${j.id}`}
                      >
                        {formatCountdown(j.next.secondsRemaining, elapsed)}
                      </span>
                    ) : (
                      <span className={styles.muted}>{j.next.text}</span>
                    )}
                  </td>
                  <td>
                    {j.workersAvailable === null ? (
                      <span
                        className={styles.muted}
                        data-testid={`flp-workers-${j.id}`}
                      >
                        —
                      </span>
                    ) : j.clickable ? (
                      <button
                        type="button"
                        className={styles.btnPlain}
                        style={{ fontSize: 'inherit' }}
                        data-testid={`flp-workers-${j.id}`}
                        aria-label={`View ${j.workersAvailable} workers available for ${j.title}`}
                        onClick={() => onSelectJob(j.id, 'workers')}
                      >
                        {j.workersAvailable}
                      </button>
                    ) : (
                      <span
                        data-testid={`flp-workers-${j.id}`}
                        title={NOT_YET_EVALUATED_TITLE}
                      >
                        {j.workersAvailable}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PreviewPagination
          pagination={pagination}
          onPageChange={onPageChange}
          itemsLabel="jobs"
        />
      </>
    )}
  </div>
);

export default JobsCard;
